import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, Minus } from "lucide-react";
import Logo from "@/components/shared/Logo";
import { pricingPlans } from "@/components/landing/pricing-data";
import { useI18n } from "@/lib/i18n";
import {
  DEFAULT_LANGUAGE_CODE,
  getUiLanguageCatalog,
} from "@/lib/languages";
import {
  buildPlanComparisonCtaHref,
  buildPlanComparisonPath,
  getPlanComparisonCardDetail,
  PLAN_COMPARISON_NOTES,
  PLAN_COMPARISON_DOCUMENT_TITLE,
  PLAN_COMPARISON_ROWS,
  PLAN_COMPARISON_STATUS,
} from "@/lib/plan-comparison";
import "./plans-redesign.css";

const PLAN_COMPARISON_GROUPS = [
  { label: "Voice notes", rows: PLAN_COMPARISON_ROWS.slice(0, 10) },
  { label: "Plan", rows: PLAN_COMPARISON_ROWS.slice(10, 16) },
  { label: "Agent", rows: PLAN_COMPARISON_ROWS.slice(16) },
];

const statusIcons = {
  [PLAN_COMPARISON_STATUS.included]: Check,
  [PLAN_COMPARISON_STATUS.limited]: Check,
  [PLAN_COMPARISON_STATUS.unavailable]: Minus,
  [PLAN_COMPARISON_STATUS.soon]: Check,
};

function normalizePlanCell(value) {
  if (value && typeof value === "object") {
    return value;
  }
  const label = String(value || "Not available");
  return /^not available$/i.test(label)
    ? { status: PLAN_COMPARISON_STATUS.unavailable, label }
    : { status: PLAN_COMPARISON_STATUS.included, label };
}

function PlanStatus({ labelledBy = "", labelId = "", planSlug, value, tx }) {
  const cell = normalizePlanCell(value);
  const Icon = statusIcons[cell.status] || Minus;

  return (
    <div
      className={`plans-status plans-status--${cell.status}${planSlug === "agent" ? " plans-status--agent" : ""}`}
      data-plan={planSlug}
      aria-labelledby={labelledBy ? `${labelledBy} ${labelId}` : undefined}
    >
      <Icon aria-hidden="true" />
      <span id={labelId || undefined}>{tx(cell.label)}</span>
    </div>
  );
}

function PageHeadLinks({ locale, title }) {
  useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined;
    }

    const previousTitle = document.title;
    document.title = title;
    const createdLinks = [];
    const origin = window.location.origin;
    const appendHeadLink = ({ rel, href, hreflang = "" }) => {
      const link = document.createElement("link");
      link.rel = rel;
      link.href = href;
      if (hreflang) link.hreflang = hreflang;
      link.dataset.mincoolPlanHead = "true";
      document.head.appendChild(link);
      createdLinks.push(link);
    };

    appendHeadLink({ rel: "canonical", href: `${origin}${buildPlanComparisonPath(locale)}` });
    appendHeadLink({
      rel: "alternate",
      href: `${origin}${buildPlanComparisonPath(DEFAULT_LANGUAGE_CODE)}`,
      hreflang: "x-default",
    });
    appendHeadLink({
      rel: "alternate",
      href: `${origin}${buildPlanComparisonPath(DEFAULT_LANGUAGE_CODE)}`,
      hreflang: DEFAULT_LANGUAGE_CODE,
    });
    for (const language of getUiLanguageCatalog()) {
      if (language.code !== DEFAULT_LANGUAGE_CODE) {
        appendHeadLink({
          rel: "alternate",
          href: `${origin}${buildPlanComparisonPath(language.code)}`,
          hreflang: language.code,
        });
      }
    }

    return () => {
      document.title = previousTitle;
      createdLinks.forEach((link) => link.remove());
    };
  }, [locale, title]);

  return null;
}

function PlanHeader({ isRevealed, plan, tx }) {
  const ctaHref = buildPlanComparisonCtaHref(plan.slug);

  return (
    <article
      className={`plans-tier${plan.popular ? " plans-tier--popular" : ""}${isRevealed ? " is-revealed" : ""}`}
      data-plan-tier={plan.slug}
    >
      <div className="plans-tier__heading">
        <h2>{tx(plan.name)}</h2>
        {plan.popular ? <span className="plans-tier__badge">{tx("Start here")}</span> : null}
        {plan.statusLabel ? <span className="plans-tier__badge plans-tier__badge--soon">{tx(plan.statusLabel)}</span> : null}
      </div>
      <p className="plans-tier__price">
        <strong>{plan.price}</strong>
        <span>{tx(plan.period)}</span>
      </p>
      <p className="plans-tier__detail">{tx(getPlanComparisonCardDetail(plan))}</p>
      {ctaHref ? (
        <Link
          className={`plans-tier__cta${plan.popular ? " plans-tier__cta--primary" : ""}`}
          to={ctaHref}
        >
          {tx(plan.cta)}
          <ArrowRight aria-hidden="true" />
        </Link>
      ) : (
        <span className="plans-tier__cta plans-tier__cta--disabled" aria-disabled="true">
          {tx(plan.cta)}
        </span>
      )}
    </article>
  );
}

function MobileTierLadder({ onSelect, selectedSlug, tx }) {
  const selectedPlan = pricingPlans.find((plan) => plan.slug === selectedSlug) || pricingPlans[1];

  return (
    <div className="plans-mobile-tier-selector">
      <section className="plans-mobile-tier-ladder" aria-label={tx("Choose a plan")}>
        {pricingPlans.map((plan) => {
          const isSelected = plan.slug === selectedPlan.slug;
          const panelId = `plans-mobile-tier-${plan.slug}`;
          const ctaHref = buildPlanComparisonCtaHref(plan.slug);

          return (
            <article
              className={`plans-mobile-tier${isSelected ? " is-selected" : ""}`}
              data-mobile-plan={plan.slug}
              key={plan.slug}
            >
              <button
                type="button"
                aria-controls={panelId}
                aria-expanded={isSelected}
                onClick={() => onSelect(plan.slug)}
              >
                <span>
                  <strong>{tx(plan.name)}</strong>
                  <small>{tx(getPlanComparisonCardDetail(plan))}</small>
                </span>
                <span className="plans-mobile-tier__price">
                  <strong>{plan.price}</strong>
                  <small>{tx(plan.period)}</small>
                </span>
                <ChevronDown aria-hidden="true" />
              </button>
              <div
                className="plans-mobile-tier__collapse"
                id={panelId}
                aria-hidden={!isSelected}
              >
                <div className="plans-mobile-tier__collapse-inner">
                  <div className="plans-mobile-tier__body">
                    <ul>
                      {plan.features.map((feature) => (
                        <li className={feature.included ? "" : "is-unavailable"} key={feature.key}>
                          {feature.included ? <Check aria-hidden="true" /> : <Minus aria-hidden="true" />}
                          <span>{tx(feature.text)}</span>
                        </li>
                      ))}
                    </ul>
                    {ctaHref ? (
                      <Link
                        className="plans-mobile-tier__cta"
                        tabIndex={isSelected ? undefined : -1}
                        to={ctaHref}
                      >
                        {tx(plan.cta)}
                        <ArrowRight aria-hidden="true" />
                      </Link>
                    ) : (
                      <span
                        className="plans-mobile-tier__cta plans-mobile-tier__cta--disabled"
                        aria-disabled="true"
                      >
                        {tx(plan.cta)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
}

export default function Plans() {
  const { locale, tx } = useI18n();
  const tierRailRef = useRef(null);
  const [selectedMobilePlan, setSelectedMobilePlan] = useState("fun");
  const [revealedTiers, setRevealedTiers] = useState(() => new Set());
  const [tierMotionEnabled] = useState(() => (
    typeof window !== "undefined"
    && "IntersectionObserver" in window
    && !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ));
  const documentTitle = tx(PLAN_COMPARISON_DOCUMENT_TITLE);
  const homePath = locale === DEFAULT_LANGUAGE_CODE ? "/" : `/l/${encodeURIComponent(locale)}`;

  useEffect(() => {
    if (!tierMotionEnabled || !tierRailRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      setRevealedTiers((currentTiers) => {
        const nextTiers = new Set(currentTiers);
        let changed = false;
        entries.forEach((entry) => {
          if (entry.isIntersecting && !nextTiers.has(entry.target.dataset.planTier)) {
            nextTiers.add(entry.target.dataset.planTier);
            observer.unobserve(entry.target);
            changed = true;
          }
        });
        return changed ? nextTiers : currentTiers;
      });
    }, { threshold: 0.5 });

    const tierCards = tierRailRef.current.querySelectorAll("[data-plan-tier]");
    tierCards.forEach((tier) => observer.observe(tier));

    return () => observer.disconnect();
  }, [tierMotionEnabled]);

  return (
    <main className="min-plans">
      <PageHeadLinks locale={locale} title={documentTitle} />

      <nav className="plans-nav" aria-label={tx("Plan")}>
        <Link to={homePath} className="plans-nav__brand" aria-label={tx("Home")}>
          <Logo size="md" />
        </Link>
      </nav>

      <header className="plans-hero">
        <div className="plans-shell plans-hero__inner">
          <h1>{tx("See exactly what each plan unlocks.")}</h1>
          <div className="plans-hero__footer">
            <p>{tx("Everyone starts on Fun for month 1. After that, fall back to Free, keep Fun going, or upgrade to Pro.")}</p>
            <aside>
              <strong>{tx("1 free month of Fun")}</strong>
              <span>{tx("Every completed signup from a Min link you send adds another free month of Fun.")}</span>
            </aside>
          </div>
        </div>
      </header>

      <div className="plans-shell plans-body">
        <section
          ref={tierRailRef}
          className={`plans-tier-rail${tierMotionEnabled ? " plans-tier-rail--motion" : ""}`}
          aria-label={tx("Choose a plan")}
        >
          {pricingPlans.map((plan) => <PlanHeader key={plan.slug} plan={plan} tx={tx} isRevealed={revealedTiers.has(plan.slug)} />)}
        </section>
        <MobileTierLadder
          onSelect={setSelectedMobilePlan}
          selectedSlug={selectedMobilePlan}
          tx={tx}
        />

        <section className="plans-comparison" aria-labelledby="plans-comparison-title">
          <div className="plans-comparison__intro">
            <div>
              <p className="plans-kicker plans-kicker--ink">{tx("Full comparison")}</p>
              <h2 id="plans-comparison-title">{tx("Feature")}</h2>
            </div>
            <p>{tx("Plans are shown in USD here. PayFast charges South African rand at checkout.")}</p>
          </div>

          <div className="plans-comparison__desktop">
            <div className="plans-ledger__header">
              <span>{tx("Feature")}</span>
              {pricingPlans.map((plan) => (
                <span id={`plans-column-${plan.slug}`} key={plan.slug}>{tx(plan.name)}</span>
              ))}
            </div>
            {PLAN_COMPARISON_GROUPS.map((group) => (
              <div className="plans-ledger__group" key={group.label}>
                <h3>{tx(group.label)}</h3>
                {group.rows.map((row) => (
                  <div className="plans-ledger__row" key={row.key}>
                    <div className="plans-ledger__feature">
                      <strong id={`plans-feature-${row.key}`}>{tx(row.feature)}</strong>
                      <span>{tx(row.detail)}</span>
                    </div>
                    {pricingPlans.map((plan) => (
                      <PlanStatus
                        key={plan.slug}
                        labelledBy={`plans-feature-${row.key} plans-column-${plan.slug}`}
                        labelId={`plans-status-${row.key}-${plan.slug}`}
                        planSlug={plan.slug}
                        value={row[plan.slug]}
                        tx={tx}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>

        </section>

        <section className="plans-close">
          <div>
            <p className="plans-kicker plans-kicker--ink">{tx("Plan")}</p>
            <ul>
              {PLAN_COMPARISON_NOTES.map((note) => (
                <li key={note}><Check aria-hidden="true" /><span>{tx(note)}</span></li>
              ))}
            </ul>
          </div>
          <div className="plans-close__actions">
            <Link className="plans-tier__cta plans-tier__cta--primary" to={buildPlanComparisonCtaHref("fun")}>
              {tx("Start 1 month free")}<ArrowRight aria-hidden="true" />
            </Link>
            <Link className="plans-close__pro" to={buildPlanComparisonCtaHref("pro")}>{tx("Start with Pro")}</Link>
          </div>
        </section>
      </div>
    </main>
  );
}
